import { ArrowUpRight } from "lucide-react";
import { Chapter, MaskedLines, Fade } from "./motion";

const WaveformIcon = () => (
    <svg viewBox="0 0 48 48" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        {[6, 14, 22, 30, 38].map((x, i) => (
            <line key={x} x1={x} y1={24 - [8, 16, 12, 18, 7][i]} x2={x} y2={24 + [8, 16, 12, 18, 7][i]} />
        ))}
    </svg>
);

const CrosshairIcon = () => (
    <svg viewBox="0 0 48 48" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <circle cx="24" cy="24" r="14" />
        <circle cx="24" cy="24" r="5" />
        <line x1="24" y1="2" x2="24" y2="10" />
        <line x1="24" y1="38" x2="24" y2="46" />
        <line x1="2" y1="24" x2="10" y2="24" />
        <line x1="38" y1="24" x2="46" y2="24" />
    </svg>
);

const DocumentIcon = () => (
    <svg viewBox="0 0 48 48" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <rect x="10" y="6" width="28" height="36" />
        <line x1="16" y1="16" x2="32" y2="16" />
        <line x1="16" y1="23" x2="32" y2="23" />
        <line x1="16" y1="30" x2="26" y2="30" />
        <polyline points="30,42 38,42 38,34" />
    </svg>
);

const DialIcon = () => (
    <svg viewBox="0 0 48 48" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <circle cx="24" cy="24" r="18" strokeDasharray="4 4" />
        <circle cx="24" cy="24" r="11" />
        <line x1="24" y1="24" x2="31" y2="15" />
        <text x="24" y="44" textAnchor="middle" fontSize="6" letterSpacing="1.5" fill="currentColor" stroke="none">24/7</text>
    </svg>
);

const MODULES = [
    {
        id: "MOD.01",
        icon: WaveformIcon,
        name: "AI Communication Lab",
        desc: "Structured speaking, writing, and presentation practice — evaluated and refined by AI, on demand.",
        spec: ["SPEECH", "WRITING", "PRESENCE"],
        span: "lg:col-span-7",
    },
    {
        id: "MOD.02",
        icon: CrosshairIcon,
        name: "AI Interviewer",
        desc: "Adaptive technical and HR interview simulations that mirror real industry rounds.",
        spec: ["TECHNICAL", "HR", "ADAPTIVE"],
        span: "lg:col-span-5",
    },
    {
        id: "MOD.03",
        icon: DocumentIcon,
        name: "Portfolio & Resume Builder",
        desc: "Assembles projects, certifications, and proof of work into industry-standard assets.",
        spec: ["PROJECTS", "CREDENTIALS", "PROOF"],
        span: "lg:col-span-5",
    },
    {
        id: "MOD.04",
        icon: DialIcon,
        name: "24/7 Practice Engine",
        desc: "An always-on environment of drills, assessments, and progression telemetry.",
        spec: ["DRILLS", "ASSESSMENTS", "TELEMETRY"],
        span: "lg:col-span-7",
    },
];

export default function Platform() {
    return (
        <section id="platform" className="blueprint-grid-dark bg-navy py-20 text-white md:py-40" data-testid="platform-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Chapter index="CH.03" title="The QRUS Platform" dark />

                <div className="mt-16 grid gap-14 md:mt-24 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-7">
                        <MaskedLines
                            stagger={0.13}
                            lineClassName="font-display text-[clamp(2.2rem,7.5vw,5.4rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-white"
                            lines={[
                                <span key="a">One system.</span>,
                                <span key="b" className="text-sky">Four instruments.</span>,
                            ]}
                        />
                    </div>
                    <div className="flex flex-col justify-end lg:col-span-5">
                        <Fade delay={0.35}>
                            <p className="text-base leading-relaxed text-sky/70 md:text-lg">
                                The QRUS platform is an integrated AI environment. Each module
                                compounds the others — practice feeds evaluation, evaluation feeds
                                the portfolio, the portfolio feeds the interview.
                            </p>
                        </Fade>
                    </div>
                </div>

                <div className="mt-16 grid gap-5 md:mt-24 lg:grid-cols-12">
                    {MODULES.map((mod, i) => (
                        <Fade key={mod.id} delay={0.1 + i * 0.08} className={mod.span}>
                            <div
                                data-testid={`platform-panel-${i + 1}`}
                                className="group relative flex h-full flex-col justify-between border border-white/15 bg-white/[0.03] p-6 transition-colors duration-500 hover:border-sky/60 md:p-10"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="text-sky/80 transition-colors duration-500 group-hover:text-sky">
                                        <mod.icon />
                                    </span>
                                    <span className="font-display text-xs font-semibold tracking-[0.25em] text-sky/50">
                                        {mod.id}
                                    </span>
                                </div>
                                <div className="mt-14 md:mt-20">
                                    <h3 className="flex items-center gap-3 font-display text-2xl font-semibold tracking-tight text-white md:text-3xl">
                                        {mod.name}
                                        <ArrowUpRight className="h-5 w-5 text-sky opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-x-1 group-hover:opacity-100" />
                                    </h3>
                                    <p className="mt-4 max-w-md text-sm leading-relaxed text-sky/60 md:text-base">
                                        {mod.desc}
                                    </p>
                                    <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-5">
                                        {mod.spec.map((s, j) => (
                                            <span key={s} className="flex items-center gap-3">
                                                <span className="text-[10px] uppercase tracking-[0.25em] text-sky/50">{s}</span>
                                                {j < mod.spec.length - 1 && <span className="text-sky/30">/</span>}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Fade>
                    ))}
                </div>
            </div>
        </section>
    );
}
